import useMountEffect from '@shared/hooks/useMountEffect';
import { createScriptLoader } from '@shared/util/dom';
import { getConfigString, TEST } from '@shared/util/env';
import useOauthLogin from '../useOauthLogin';

const loader = createScriptLoader('fb-sdk');

const inject = () => {
  if (TEST) {
    return Promise.resolve(true);
  }
  const appId = getConfigString('fb-app-id');
  if (!appId) {
    window.FB = {
      login: (cb: any) => cb({ authResponse: true, status: 'connected' }),
      getLoginStatus: (cb: any) => cb({ status: 'unknown' }),
      logout: (cb: any) => cb({}),
    } as any;
    return;
  }
  (window as any).fbAsyncInit = () => {
    FB.init({
      appId,
      version: 'v20.0',
      cookie: true,
    });
  };
  return loader('https://connect.facebook.net/en_US/sdk.js');
};

const Facebook = () => {
  const provider = 'facebook';
  const login = useOauthLogin();

  const onSignIn = async (locale: string) => {
    if (TEST) {
      return login({ provider, locale });
    }

    await inject();
    return new Promise<ReturnType<typeof login>>((resolve, reject) =>
      window.FB.getLoginStatus((response) => {
        if (response.status === 'connected') {
          resolve(login({ provider, locale }));
        }
        if (['not_authorized', 'unknown'].includes(response.status)) {
          window.FB.login((response) => {
            if (response.status === 'connected') {
              resolve(login({ provider, locale }));
            } else {
              reject({ success: false });
            }
          });
        }
      }),
    );
  };

  useMountEffect(() => void inject());

  return {
    provider,
    onSignIn,
  };
};

export const reAuthenticateFacebook = async (nonce: string) => {
  if (TEST) {
    return new Promise<string>((resolve) => {
      resolve(nonce);
    });
  }

  await inject();

  return new Promise<string | null>((resolve) => {
    FB.login(
      (response) => {
        if (response.status === 'connected') {
          resolve(response.authResponse.accessToken ?? null);
        }
        resolve(null);
      },
      { auth_type: 'reauthenticate', auth_nonce: nonce },
    );
  });
};

const logoutPromise = () =>
  new Promise((resolve) => {
    FB.getLoginStatus((response) => {
      if (response.status === 'connected') {
        window.FB.logout(() => resolve(true));
      } else {
        resolve(false);
      }
    });
  });

export const logoutFromFacebook = async () => {
  if (TEST) {
    return;
  }
  await inject();
  await logoutPromise();
};

export default Facebook;
